import React from "react";
import {useParams, useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {BsArrowLeft, BsEnvelopeFill, BsGearFill, BsPersonFill, BsAwardFill, BsChevronDown, BsBoxArrowRight} from "react-icons/bs";
import {RiPhoneFill} from "react-icons/ri";
import {useGroupManager} from "../../Providers/groupManager";
import {formatPhone} from "../../../Utils/phoneHelpers";
import WS from "../../../ws";
import CampaignSwitcher from "../CampaignSwitcher";
import {
    Container,
    Left,
    BackBtn,
    BackTooltip,
    Divider,
    Logo,
    TitleBox,
    Title,
    Phone,
    Status,
    StatusDot,
    Tabs,
    Tab,
    Right,
    MenuWrap,
    UserMenu,
    Avatar,
    UserInfo,
    UserName,
    UserRole,
    Caret,
    Dropdown,
    DropdownItem,
} from "./styles";

/**
 * Barra de topo da tela Group Manager.
 * Visual conforme o design. Seletor de campanha, telefone e menu do usuário são
 * placeholders estáticos por enquanto. As abas navegam de verdade (sub-rotas) e
 * marcam a ativa.
 */
// Iniciais das 2 primeiras palavras do nome da campanha, para o logo.
const initials = (name) => {
    if (!name) return "";
    return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
};

const ManagerTopBar = () => {
    const {id} = useParams();
    const history = useHistory();
    const {t} = useTranslation();
    const base = `/campaign/${id}/manager`;
    const {campaign, number, loading, isOwner, fullName, role} = useGroupManager();
    const phone = loading ? "…" : (number?.number ? formatPhone(number.number) : "—");
    const logoInitials = initials(campaign?.name) || "GM";
    const userInitials = initials(fullName) || "GM";
    const [menuOpen, setMenuOpen] = React.useState(false);
    const menuRef = React.useRef(null);

    React.useEffect(() => {
        if (!menuOpen) return undefined;
        const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [menuOpen]);

    const logout = () => {
        WS.close();
        localStorage.removeItem("token");
        localStorage.removeItem("operator_session_token");
        // Ramifica por papel: sem isso o dono cairia preso no formulário de chave do operador.
        if (isOwner) {
            // Dono do app: volta pro Office (não há login de owner no desktop — "/" cai no 404 do LoginChat).
            window.location.href = "https://office.builderall.com";
            return;
        }
        // Operador: página de login dedicada da tool.
        history.push(`/operator/login/${id}`);
    };

    return (
        <Container>
            <Left>
                <BackTooltip title={t("words.back_to_campaign")} placement="bottom" arrow>
                    <BackBtn onClick={() => history.push(`/campaign/${id}/detail`)}
                             aria-label={t("words.back_to_campaign")}>
                        <BsArrowLeft/>
                    </BackBtn>
                </BackTooltip>

                <Divider/>

                <Logo>{logoInitials}</Logo>

                <TitleBox>
                    <Title>{t("words.group_manager")}</Title>
                    <CampaignSwitcher/>
                </TitleBox>

                <Phone>
                    <RiPhoneFill/> {phone}
                    {!loading && (
                        <Status>
                            <StatusDot $on={!!number?.connected}/>
                            {number?.connected ? t("words.conected") : t("words.disconnected")}
                        </Status>
                    )}
                </Phone>
            </Left>

            <Tabs>
                <Tab to={`${base}/inbox`}>
                    <BsEnvelopeFill/> {t("words.inbox")}
                </Tab>
                <Tab to={`${base}/settings`}><BsGearFill/> {t("words.group_settings")}</Tab>
                <Tab to={`${base}/members`}><BsPersonFill/> {t("words.members")}</Tab>
                {isOwner && (
                    <Tab to={`${base}/operators`}><BsAwardFill/> {t("words.operator_access")}</Tab>
                )}
            </Tabs>

            <Right>
                <MenuWrap ref={menuRef}>
                    <UserMenu type="button" onClick={() => setMenuOpen((v) => !v)}>
                        <Avatar>{userInitials}</Avatar>
                        <UserInfo>
                            <UserName>{fullName || "—"}</UserName>
                            <UserRole>{role ? t(`words.role_${role}`) : ""}</UserRole>
                        </UserInfo>
                        <Caret><BsChevronDown/></Caret>
                    </UserMenu>
                    {menuOpen && (
                        <Dropdown>
                            <DropdownItem type="button" onClick={logout}>
                                <BsBoxArrowRight/> {t("words.logout")}
                            </DropdownItem>
                        </Dropdown>
                    )}
                </MenuWrap>
            </Right>
        </Container>
    );
};

export default ManagerTopBar;
